import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MapPin, Calendar, Send, Users, Clock, Plus, X, Lock } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import { formatTime12hr, formatEventDate, parseEventDate } from '../../utils/formatting';
import DateInput from '../common/DateInput';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';

const MyEventsView = () => {
  const { events, setEvents, users, currentUser, conversations, setConversations, groups, setView, showAlert } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', date: '', time: '', course: '',
    address: '', addressLat: null, addressLon: null, maxPlayers: 4, visibility: 'public'
  });

  const now = new Date();

  // Get events the current user has joined
  const myEvents = events.filter(event => event.attendees.includes(currentUser.id));

  // Get invited events (exclude past and ended events)
  const invitedEvents = events.filter(event =>
    event.status !== 'ended' &&
    parseEventDate(event.date) >= now &&
    !event.attendees?.includes(currentUser.id) &&
    (event.invitedUsers?.includes(currentUser.id) ||
      event.invitedGroups?.some(gId => {
        const g = groups.find(gr => gr.id === gId);
        return g?.members?.includes(currentUser.id);
      }))
  );

  // Split into current and past events based on date or ended status
  const currentEvents = myEvents.filter(event => event.status !== 'ended' && parseEventDate(event.date) >= now);
  const pastEvents = myEvents.filter(event => event.status === 'ended' || parseEventDate(event.date) < now);

  // Sort current events by date (soonest first), past events by date (most recent first)
  const sortedCurrentEvents = currentEvents.sort((a, b) => parseEventDate(a.date) - parseEventDate(b.date));
  const sortedPastEvents = pastEvents.sort((a, b) => parseEventDate(b.date) - parseEventDate(a.date));

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.time || !newEvent.course || !newEvent.address) return;
    try {
      const eventData = {
        userId: currentUser.id,
        title: SecurityUtils.sanitizeText(newEvent.title),
        description: SecurityUtils.sanitizeText(newEvent.description || ''),
        date: newEvent.date,
        time: newEvent.time,
        course: SecurityUtils.sanitizeText(newEvent.course),
        address: SecurityUtils.sanitizeText(newEvent.address),
        addressLat: newEvent.addressLat || null,
        addressLon: newEvent.addressLon || null,
        maxPlayers: newEvent.maxPlayers,
        attendees: [currentUser.id],
        visibility: newEvent.visibility,
      };
      const createdEvent = await firestoreService.createEvent(eventData);
      const eventDateFormatted = formatEventDate(newEvent.date);
      const groupChat = await firestoreService.createGroupChat(createdEvent.id, newEvent.title, eventDateFormatted, currentUser.id, []);
      await firestoreService.updateEvent(createdEvent.id, { groupChatId: groupChat.id });
      setShowCreateModal(false);
      setNewEvent({ title: '', description: '', date: '', time: '', course: '', address: '', addressLat: null, addressLon: null, maxPlayers: 4, visibility: 'public' });
    } catch (err) {
      console.error('Error creating event:', err);
      showAlert('Failed to create event. Please try again.', 'Error');
    }
  };

  const handleLeaveEvent = async (eventId) => {
    const targetEvent = events.find(e => e.id === eventId);
    if (targetEvent && targetEvent.userId === currentUser.id) return;
    setEvents(prev => prev.map(event => event.id === eventId ? { ...event, attendees: event.attendees.filter(id => id !== currentUser.id) } : event));
    try {
      await firestoreService.leaveEvent(eventId, currentUser.id);
      const groupChat = await firestoreService.getConversationByEventId(eventId);
      if (groupChat) await firestoreService.removeParticipant(groupChat.id, currentUser.id);
    } catch (err) { console.error('Error leaving event:', err); }
  };

  const renderEvent = (event) => {
    const organizer = users.find(u => u.id === event.userId);
    const isPast = event.status === 'ended' || parseEventDate(event.date) < now;

    return (
      <div key={event.id} className={`bg-black rounded-lg shadow border ${isPast ? 'border-gray-800' : 'border-gray-700'} p-6`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white cursor-pointer hover:text-[#16a34a] transition-colors" onClick={() => setView('event-detail-' + event.id)}>{event.title}</h3>
            {event.edited && (
              <p className="text-xs text-white italic">Edited</p>
            )}
          </div>
          {!isPast && event.userId !== currentUser.id && (
            <button
              onClick={() => handleLeaveEvent(event.id)}
              className="px-3 py-1 bg-red-600 text-pure-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              Leave Event
            </button>
          )}
        </div>
        <p className="text-sm text-white mb-3">By {organizer?.name} (@{organizer?.handle})</p>
        <p className="text-white mb-4">{event.description}</p>
        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2 text-sm">
            <Calendar className="w-4 h-4 text-white" />
            <span className="text-white">{formatEventDate(event.date)} at {formatTime12hr(event.time)}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <MapPin className="w-4 h-4 text-white" />
            <div>
              <div className="text-white">{event.course}</div>
              {event.address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {event.address}
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Users className="w-4 h-4 text-white" />
            <span className="text-white">{event.attendees.length} / {event.maxPlayers} players</span>
          </div>
        </div>
        {isPast && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-2 text-center">
            <p className="text-xs text-white">{event.status === 'ended' ? 'This event has ended' : 'This event has passed'}</p>
          </div>
        )}
      </div>
    );
  };

  const handleAcceptInvite = async (eventId) => {
    try {
      const event = events.find(e => e.id === eventId);
      if (!event || event.attendees?.length >= event.maxPlayers) return;
      await firestoreService.joinEvent(eventId, currentUser.id);
      await firestoreService.removeEventInvite(eventId, currentUser.id);
      if (event.groupChatId) {
        await firestoreService.addParticipant(event.groupChatId, currentUser.id);
      }
    } catch (err) { console.error('Error accepting invite:', err); }
  };

  const handleDeclineInvite = async (eventId) => {
    try {
      await firestoreService.removeEventInvite(eventId, currentUser.id);
    } catch (err) { console.error('Error declining invite:', err); }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-white">My Events</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-3 py-1.5 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Create</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        {['upcoming', 'past', 'invited'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize ${activeTab === tab ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white'}`}>
            {tab} {tab === 'invited' && invitedEvents.length > 0 && <span className="ml-1 bg-red-600 text-pure-white rounded-full px-1.5 text-xs">{invitedEvents.length}</span>}
          </button>
        ))}
      </div>

      {activeTab === 'upcoming' && (
        <div>
          {sortedCurrentEvents.length === 0 ? (
            <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
              <Calendar className="w-12 h-12 text-white mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-2">No upcoming events</h3>
              <p className="text-white">You haven't joined any upcoming events yet.</p>
            </div>
          ) : (
            <div className="space-y-4">{sortedCurrentEvents.map(renderEvent)}</div>
          )}
        </div>
      )}

      {activeTab === 'past' && (
        <div>
          {sortedPastEvents.length === 0 ? (
            <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
              <Clock className="w-12 h-12 text-white mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-2">No past events</h3>
              <p className="text-white">Your event history will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">{sortedPastEvents.map(renderEvent)}</div>
          )}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-black rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Create Golf Event</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-white hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Event Title *</label>
                <input type="text" required value={newEvent.title} onChange={(e) => setNewEvent({...newEvent, title: e.target.value})} className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black" placeholder="Sunday Morning Golf" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <textarea value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black" rows="3" placeholder="All skill levels welcome..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Golf Course *</label>
                <input type="text" required value={newEvent.course} onChange={(e) => setNewEvent({...newEvent, course: e.target.value})} className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black" placeholder="Van Cortlandt Park" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Address *</label>
                <GooglePlacesAutocomplete
                  value={newEvent.address}
                  onChange={(value) => setNewEvent({...newEvent, address: value})}
                  onSelect={(loc) => setNewEvent(prev => ({...prev, address: loc.displayName, addressLat: loc.lat, addressLon: loc.lon}))}
                  placeholder="Start typing the course address..."
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  required={true}
                />
                <p className="text-xs text-gray-400 mt-1">Type the address and select from suggestions</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Date *</label>
                  <DateInput
                    value={newEvent.date}
                    onChange={(v) => setNewEvent({...newEvent, date: v})}
                    min={`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`}
                    required
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Time *</label>
                  <input type="time" required value={newEvent.time} onChange={(e) => setNewEvent({...newEvent, time: e.target.value})} className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Max Players *</label>
                <input type="number" required min="2" max="8" value={newEvent.maxPlayers} onChange={(e) => setNewEvent({...newEvent, maxPlayers: parseInt(e.target.value)})} className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Visibility</label>
                <div className="flex space-x-2">
                  <button type="button" onClick={() => setNewEvent({...newEvent, visibility: 'public'})} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${newEvent.visibility === 'public' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}>Public</button>
                  <button type="button" onClick={() => setNewEvent({...newEvent, visibility: 'private'})} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${newEvent.visibility === 'private' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}><Lock className="w-4 h-4" /><span>Private</span></button>
                </div>
                <p className="text-xs text-gray-400 mt-1">{newEvent.visibility === 'private' ? 'Only invited users can see and join this event' : 'Anyone can see and join this event'}</p>
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-3 py-2 border border-gray-700 text-white rounded-lg hover:bg-gray-900">Cancel</button>
                <button type="button" onClick={handleCreateEvent} className="flex-1 px-3 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]">Create Event</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'invited' && (
        <div>
          {invitedEvents.length === 0 ? (
            <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
              <Send className="w-12 h-12 text-white mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-2">No invites</h3>
              <p className="text-white">You haven't been invited to any events yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {invitedEvents.map(event => {
                const organizer = users.find(u => u.id === event.userId);
                return (
                  <div key={event.id} className="bg-black rounded-lg shadow border border-yellow-600 p-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 bg-yellow-600 text-pure-white text-xs rounded-full font-bold">Invited</span>
                    </div>
                    <h3 className="text-lg font-bold text-white cursor-pointer hover:text-[#16a34a] transition-colors" onClick={() => setView('event-detail-' + event.id)}>{event.title}</h3>
                    <p className="text-sm text-white mb-3">By {organizer?.name} (@{organizer?.handle})</p>
                    <p className="text-white mb-3">{event.description}</p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <Calendar className="w-4 h-4 text-white" />
                        <span className="text-white">{formatEventDate(event.date)} at {formatTime12hr(event.time)}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <MapPin className="w-4 h-4 text-white" />
                        <span className="text-white">{event.course}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Users className="w-4 h-4 text-white" />
                        <span className="text-white">{event.attendees?.length || 0} / {event.maxPlayers} players</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => handleAcceptInvite(event.id)} disabled={event.attendees?.length >= event.maxPlayers} className="flex-1 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50">
                        {event.attendees?.length >= event.maxPlayers ? 'Event Full' : 'Accept & Join'}
                      </button>
                      <button onClick={() => handleDeclineInvite(event.id)} className="flex-1 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">
                        Decline
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== GROUPS ====================

export default MyEventsView;
